// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Stepper component for the item data upload process
 */
import { Check } from '@/constants/hardfin-standard-icons';
import type { Step } from '@/items/components/item-data-upload/types';
import { Box, Stack, Typography } from '@mui/material';

interface StepperProps {
  currentStep: Step;
}

/**
 * Renders a stepper component to visualize the current step in the upload process
 */
export function Stepper({ currentStep }: StepperProps) {
  const steps: { key: Step; label: string }[] = [
    { key: 'download-template', label: 'Download template' },
    { key: 'upload-csv', label: 'Upload CSV' },
    { key: 'results', label: 'Results' },
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'divider',
            zIndex: 0,
          },
        }}
      >
        {steps.map((step, index) => {
          const isActive = currentStep === step.key;
          const currentIndex = steps.findIndex((s) => s.key === currentStep);
          const isCompleted = index < currentIndex;

          return (
            <Box
              key={step.key}
              sx={{
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: 'white',
                px: 3,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  backgroundColor: isCompleted
                    ? 'success.main'
                    : isActive
                      ? 'primary.main'
                      : 'background.paper',
                  border: (theme) =>
                    `2px solid ${
                      isCompleted
                        ? theme.palette.success.main
                        : isActive
                          ? theme.palette.primary.main
                          : theme.palette.divider
                    }`,
                  color: isCompleted || isActive ? 'white' : 'text.secondary',
                  mb: 1,
                }}
              >
                {isCompleted ? <Check /> : index + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: isActive ? 'bold' : 'normal',
                  color: isActive ? 'primary.main' : 'text.secondary',
                  textAlign: 'center',
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
